import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { SUBJECT_META_ALL, levelStyle, overallBand, TARGET_STATUS } from "@/lib/scale";
import { TestScores, RunningLatest, ProgressTracker } from "@/components/student/ReportSections";
import { Printer, ArrowLeft, Loader2, Sprout } from "lucide-react";
import DocHeader from "@/components/DocHeader";

export default function Report() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [student, setStudent] = useState(null);
  const [tests, setTests] = useState([]);
  const [reading, setReading] = useState([]);
  const [records, setRecords] = useState([]);

  useEffect(() => {
    api.get(`/students/${studentId}`).then((r) => setStudent(r.data)).catch(() => navigate("/"));
    api.get(`/standardized-tests?student_id=${studentId}`).then((r) => setTests(r.data || [])).catch(() => {});
    api.get(`/reading-records?student_id=${studentId}`).then((r) => setReading(r.data || [])).catch(() => {});
    api.get(`/checklist-records?student_id=${studentId}`).then((r) => setRecords(r.data || [])).catch(() => {});
  }, [studentId, navigate]);

  if (!student || !curriculum)
    return <div className="min-h-screen flex justify-center items-center text-slate-300"><Loader2 className="animate-spin" /></div>;

  const subjects = curriculum.subjects;
  const subjectKeys = Object.keys(subjects).filter((k) => !subjects[k].optional || student.is_asd);
  const today = new Date().toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#F9F9F7] py-8 px-4">
      {/* Toolbar */}
      <div className="max-w-3xl mx-auto flex items-center justify-between mb-6 no-print">
        <button
          data-testid="report-back-btn"
          onClick={() => navigate(`/student/${studentId}`)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition"
        >
          <ArrowLeft size={16} /> Back to profile
        </button>
        <button
          data-testid="print-report-btn"
          onClick={() => window.print()}
          className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2"
        >
          <Printer size={17} /> Print / Save PDF
        </button>
      </div>

      {/* Sheet */}
      <div className="print-area max-w-3xl mx-auto bg-white rounded-2xl shadow-[0_4px_24px_rgba(42,59,71,0.06)] p-8 sm:p-10">
        <DocHeader printOnly subtitle="Pupil Progress Report" />
        <div className="flex items-center justify-between border-b border-slate-100 pb-5">
          <div className="flex items-center gap-2.5">
            <span className="h-9 w-9 rounded-xl bg-[#81B29A] flex items-center justify-center text-white"><Sprout size={18} /></span>
            <span className="font-['Outfit'] font-bold text-lg text-slate-800">BloomTrack</span>
          </div>
          <p className="text-sm text-slate-400">{today}</p>
        </div>

        <h1 className="font-['Outfit'] text-3xl font-bold text-slate-800 mt-6">Student Progress Report</h1>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 text-sm">
          <Info label="Name" value={student.name} />
          <Info label="Class" value={student.class_name} />
          <Info label="Level" value={student.class_level} />
        </div>

        {subjectKeys.map((key) => {
          const subj = subjects[key];
          const latest = student.assessments.find((a) => a.subject === key);
          return (
            <section key={key} className="mt-8" data-testid={`report-section-${key}`}>
              <div className="flex items-center justify-between border-b-2 pb-2" style={{ borderColor: SUBJECT_META_ALL[key].tint }}>
                <h2 className="font-['Outfit'] text-xl font-bold text-slate-800">{subj.name}</h2>
                {latest && (
                  <span className="font-['Outfit'] font-semibold text-slate-700">
                    {latest.total}/45 · {overallBand(latest.total).name}
                  </span>
                )}
              </div>

              {latest ? (
                <>
                  <table className="w-full mt-3 text-sm">
                    <tbody>
                      {subj.domains.map((d) => {
                        const lvl = latest.scores[d.key];
                        const desc = d.levels.find((l) => l.level === lvl)?.descriptor || "";
                        const st = levelStyle(lvl);
                        return (
                          <tr key={d.key} className="border-b border-slate-50">
                            <td className="py-2 pr-3 font-semibold text-slate-700 align-top w-40">{d.name}</td>
                            <td className="py-2 pr-3">
                              <span className="inline-flex items-center justify-center h-6 w-6 rounded-md text-white text-xs font-bold" style={{ backgroundColor: st.solid }}>{lvl}</span>
                            </td>
                            <td className="py-2 text-slate-600">{desc}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {(latest.strengths || latest.areas_for_development || latest.next_steps) && (
                    <div className="grid sm:grid-cols-3 gap-3 mt-3 text-sm">
                      <Note title="Strengths" value={latest.strengths} />
                      <Note title="Areas for development" value={latest.areas_for_development} />
                      <Note title="Next steps" value={latest.next_steps} />
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-400 mt-3">Not yet assessed.</p>
              )}
            </section>
          );
        })}

        {/* Targets */}
        {student.targets?.length > 0 && (
          <section className="mt-8" data-testid="report-targets">
            <h2 className="font-['Outfit'] text-xl font-bold text-slate-800 border-b-2 border-[#F6C358] pb-2">Priority Learning Targets &amp; Recommended Next Steps</h2>
            <div className="mt-3 space-y-2.5">
              {student.targets.map((t) => {
                const meta = SUBJECT_META_ALL[t.subject];
                const dom = subjects[t.subject]?.domains.find((d) => d.key === t.domain);
                const st = TARGET_STATUS[t.status];
                return (
                  <div key={t.id} className="flex items-start gap-3 rounded-xl border border-slate-100 p-3 text-sm">
                    <span className="mt-0.5 h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: meta.tint }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-700">{t.text}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: meta.tint }}>{meta.name}</span>
                        <span className="text-xs text-slate-400">· {dom?.name}</span>
                        {t.target_level && (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            · Target
                            <span className="inline-flex items-center justify-center h-5 w-5 rounded text-white text-[11px] font-bold" style={{ backgroundColor: levelStyle(t.target_level).solid }}>{t.target_level}</span>
                          </span>
                        )}
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.name}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Test scores, running assessments & progress */}
        <TestScores tests={tests} reading={reading} />
        <RunningLatest records={records} />
        <ProgressTracker records={records} />

        <p className="text-xs text-slate-400 mt-10 pt-5 border-t border-slate-100 text-center">
          Generated with BloomTrack · Aligned to the Irish primary curriculum · Scale 1–9 per skill area
        </p>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      <p className="font-semibold text-slate-800 font-['Outfit']">{value || "—"}</p>
    </div>
  );
}

function Note({ title, value }) {
  return (
    <div className="bg-[#F9F9F7] rounded-xl p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{title}</p>
      <p className="text-slate-600 mt-1">{value || "—"}</p>
    </div>
  );
}
